import { create } from 'zustand';
import qs from 'qs';
import { News, NewsComment, CreateNewsData, UpdateNewsData } from '@/types';
import { uploadFilesSequentially } from '@/lib/upload';
import { fetchStrapiCollectionByIds, readCreatedDocumentId } from '@/lib/strapi';

const baseUrl = '/api/strapi';

const newsPopulate = {
  images: { fields: ['url', 'alternativeText'] },
  author: { fields: ['id', 'displayName', 'username', 'initials', 'avatarUrl', 'avatarColor'] },
  liked_by: { fields: ['id'] },
  news_comments: { count: true },
  group: {
    fields: ['id', 'documentId', 'name'],
    populate: {
      avatar: { fields: ['url', 'alternativeText'] }
    }
  },
};

const commentPopulate = {
  news: { fields: ['documentId'] },
  author: { fields: ['id', 'displayName', 'username', 'initials', 'avatarUrl', 'avatarColor'] },
  liked_by: { fields: ['id'] },
  parentComment: { fields: ['documentId'] },
};

function userHasLiked(relation: { id: number }[] | undefined, userId: number): boolean {
  return relation?.some((user) => user.id === userId) ?? false;
}

/** Copy of `source` without `keys`, or `source` itself when nothing would change. */
function withoutKeys(source: Record<string, boolean>, keys: string[]): Record<string, boolean> {
  if (!keys.some((key) => key in source)) return source;
  const next = { ...source };
  keys.forEach((key) => delete next[key]);
  return next;
}

interface NewsResponse {
  data: News[];
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

interface NewsCommentsResponse {
  data: NewsComment[];
}

interface CreateCommentParams {
  newsDocumentId: string;
  content: string;
  parentCommentDocumentId?: string;
}

interface NewsState {
  news: News[];
  groupNewsByGroupDocumentId: Record<string, News[]>;
  commentsByNews: Record<string, NewsComment[]>;
  /**
   * Posts whose comment load failed, keyed by news documentId. Lets the UI
   * distinguish "no comments" from "we could not load them" (MWBI-934); a
   * later successful load for the same post clears its entry.
   */
  commentsFailedFor: Record<string, boolean>;
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  hasMore: boolean;

  fetchNews: (page?: number) => Promise<void>;
  fetchGroupNews: (groupDocumentId: string) => Promise<void>;
  fetchCommentsBatch: (newsDocumentIds: string[]) => Promise<void>;
  fetchComments: (newsDocumentId: string) => Promise<void>;
  /** On success, `documentId` is the new post's id (for the activity log), when Strapi returned one. */
  createNews: (data: CreateNewsData) => Promise<{ success: boolean; error?: string; documentId?: string }>;
  updateNews: (documentId: string, data: UpdateNewsData) => Promise<{ success: boolean; error?: string }>;
  deleteNews: (documentId: string) => Promise<boolean>;
  createComment: (params: CreateCommentParams) => Promise<boolean>;
  updateComment: (newsDocumentId: string, commentDocumentId: string, content: string) => Promise<boolean>;
  deleteComment: (newsDocumentId: string, commentDocumentId: string) => Promise<boolean>;
  toggleLike: (newsDocumentId: string) => Promise<void>;
  toggleCommentLike: (newsDocumentId: string, commentDocumentId: string) => Promise<void>;
  hasUserLiked: (newsDocumentId: string, userId: number) => boolean;
  hasUserLikedComment: (newsDocumentId: string, commentDocumentId: string, userId: number) => boolean;
  setSelectedCategory: (category: string | null) => void;
  getSortedNews: (isFollowedGroup: (id: string) => boolean) => News[];
}

export const useNewsStore = create<NewsState>((set, get) => ({
  news: [],
  groupNewsByGroupDocumentId: {},
  commentsByNews: {},
  commentsFailedFor: {},
  loading: false,
  error: null,
  currentPage: 1,
  totalPages: 1,
  hasMore: true,

  fetchNews: async (page = 1) => {
    try {
      set({ loading: true, error: null });

      const query = {
        populate: newsPopulate,
        sort: ['createdAt:desc'],
        filters: { deletedAt: { $null: true } },
        pagination: { page, pageSize: 10 },
      };

      const queryString = qs.stringify(query, { encodeValuesOnly: true });
      // eslint-disable-next-line local/no-unpaginated-strapi-fetch -- intentional page-by-page news feed pagination (load-more UI)
      const res = await fetch(`${baseUrl}/news-posts?${queryString}`, { cache: 'no-store' });
      if (!res.ok) throw new Error('Failed to fetch news');

      const data: NewsResponse = await res.json();
      
      set((state) => ({
        news: page === 1 ? data.data : [...state.news, ...data.data],
        currentPage: page,
        totalPages: data.meta.pagination.pageCount,
        hasMore: page < data.meta.pagination.pageCount,
        loading: false,
      }));
    } catch (err) {
      console.error('src/store/newsStore.ts', err);
      set({ 
        error: err instanceof Error ? err.message : 'Error fetching news', 
        loading: false 
      });
    }
  },

  fetchGroupNews: async (groupDocumentId: string) => {
    try {
      const query = {
        populate: newsPopulate,
        sort: ['createdAt:desc'],
        filters: {
          deletedAt: { $null: true },
          group: { documentId: { $eq: groupDocumentId } },
        },
        pagination: { page: 1, pageSize: 50 },
      };

      const queryString = qs.stringify(query, { encodeValuesOnly: true });
      const url = `${baseUrl}/news-posts?${queryString}`;

      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error('Failed to fetch group news');

      const data: NewsResponse = await res.json();
      set((state) => ({
        groupNewsByGroupDocumentId: {
          ...state.groupNewsByGroupDocumentId,
          [groupDocumentId]: data.data,
        },
      }));
    } catch (err) {
      console.error('Error fetching group news:', err);
    }
  },

  fetchCommentsBatch: async (newsDocumentIds: string[]) => {
    const uniqueNewsDocumentIds = Array.from(
      new Set(newsDocumentIds.filter((documentId): documentId is string => Boolean(documentId)))
    );

    if (uniqueNewsDocumentIds.length === 0) {
      return;
    }

    try {
      // Chunked so the `$in` list cannot grow the query string past the 2048-byte
      // ceiling AWS WAF enforces. One request for every post on a 30-post group
      // built a 2244-byte URL, which the ALB rejected with a 403 before Strapi
      // saw it -- every comment then rendered as "0 comments" (MWBI-934).
      const comments = await fetchStrapiCollectionByIds<NewsCommentsResponse['data'][number]>(
        'news-comments',
        uniqueNewsDocumentIds,
        (documentIds) => ({
          filters: {
            news: { documentId: { $in: documentIds } },
            deletedAt: { $null: true },
          },
          populate: commentPopulate,
          sort: ['createdAt:asc'],
        })
      );

      const commentsByNewsDocumentId = Object.fromEntries(
        uniqueNewsDocumentIds.map((documentId) => [documentId, [] as NewsComment[]])
      );

      comments.forEach((comment) => {
        const commentNewsDocumentId = comment.news?.documentId;

        if (!commentNewsDocumentId || !(commentNewsDocumentId in commentsByNewsDocumentId)) {
          return;
        }

        commentsByNewsDocumentId[commentNewsDocumentId].push(comment);
      });

      set((state) => ({
        commentsByNews: {
          ...state.commentsByNews,
          ...commentsByNewsDocumentId,
        },
        commentsFailedFor: withoutKeys(state.commentsFailedFor, uniqueNewsDocumentIds),
      }));
    } catch (err) {
      // A failed load must not be indistinguishable from "this post has no
      // comments" -- that silence is what let MWBI-934 sit unnoticed. Record
      // which posts are unknown so NewsCard can say so instead of showing 0.
      //
      // Only posts we know NOTHING about are marked. A post whose comments
      // loaded earlier still has valid data in commentsByNews, and downgrading
      // it to "unavailable" because an unrelated refresh failed would hide
      // comments the reader can already see.
      console.error('Error fetching comments:', err);
      set((state) => ({
        commentsFailedFor: uniqueNewsDocumentIds.reduce(
          (acc, documentId) =>
            documentId in state.commentsByNews ? acc : { ...acc, [documentId]: true },
          state.commentsFailedFor
        ),
      }));
    }
  },

  fetchComments: async (newsDocumentId: string) => {
    await get().fetchCommentsBatch([newsDocumentId]);
  },

  createNews: async (data: CreateNewsData) => {
    try {
      // Upload images first if provided
      const imageIds = await uploadFilesSequentially(data.images ?? [], 'Failed to upload images');

      const postData: Record<string, unknown> = {
        content: data.content,
        likes: 0,
        images: imageIds.length > 0 ? imageIds : null,
      };

      // Add group relation if provided
      if (data.groupDocumentId) {
        postData.group = { connect: [data.groupDocumentId] };
      }

      const res = await fetch(`${baseUrl}/news-posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: postData }),
      });

      if (!res.ok) throw new Error('Failed to create news');
      const documentId = await readCreatedDocumentId(res);

      await get().fetchNews(1);
      if (data.groupDocumentId && get().groupNewsByGroupDocumentId[data.groupDocumentId]) {
        await get().fetchGroupNews(data.groupDocumentId);
      }
      return { success: true, documentId };
    } catch (err) {
      console.error('Error creating news:', err);
      return { success: false, error: (err as Error).message };
    }
  },

  updateNews: async (documentId: string, data: UpdateNewsData) => {
    try {
      // Kept images first, then any new uploads, in the order they were picked
      const newImageIds = await uploadFilesSequentially(data.images ?? [], 'Failed to upload images');
      const imageIds = [...(data.imagesToKeep ?? []), ...newImageIds];

      const updateData: Record<string, unknown> = {};
      if (data.content !== undefined) updateData.content = data.content;
      // Always update images field - empty array if no images, or array of IDs if there are images
      updateData.images = imageIds.length > 0 ? imageIds : [];

      const res = await fetch(`${baseUrl}/news-posts/${documentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: updateData }),
      });

      if (!res.ok) throw new Error('Failed to update news');

      const loadedGroupKeys = Object.entries(get().groupNewsByGroupDocumentId)
        .filter(([, posts]) => posts.some((post) => post.documentId === documentId))
        .map(([groupDocumentId]) => groupDocumentId);

      await Promise.all([
        get().fetchNews(1),
        ...loadedGroupKeys.map((groupDocumentId) => get().fetchGroupNews(groupDocumentId)),
      ]);
      return { success: true };
    } catch (err) {
      console.error('Error updating news:', err);
      return { success: false, error: (err as Error).message };
    }
  },

  deleteNews: async (documentId: string) => {
    try {
      const res = await fetch(`${baseUrl}/news-posts/${documentId}/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!res.ok) throw new Error('Failed to delete news');

      set((state) => ({
        news: state.news.filter(n => n.documentId !== documentId),
        groupNewsByGroupDocumentId: Object.fromEntries(
          Object.entries(state.groupNewsByGroupDocumentId).map(([groupDocumentId, posts]) => [
            groupDocumentId,
            posts.filter((post) => post.documentId !== documentId),
          ])
        ),
        commentsByNews: Object.fromEntries(
          Object.entries(state.commentsByNews).filter(([key]) => key !== documentId)
        ),
        // Drop the post's failure marker with its comments -- leaving it would
        // strand an error flag for a post that no longer exists.
        commentsFailedFor: withoutKeys(state.commentsFailedFor, [documentId]),
      }));
      return true;
    } catch (err) {
      console.error('Error deleting news:', err);
      return false;
    }
  },

  createComment: async ({ newsDocumentId, content, parentCommentDocumentId }) => {
    try {
      const res = await fetch(`${baseUrl}/news-comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            content,
            likes: 0,
            news: { connect: [newsDocumentId] },
            parentComment: parentCommentDocumentId
              ? { connect: [parentCommentDocumentId] }
              : null,
          }
        }),
      });

      if (!res.ok) throw new Error('Failed to create comment');

      await get().fetchComments(newsDocumentId);
      return true;
    } catch (err) {
      console.error('Error creating comment:', err);
      return false;
    }
  },

  updateComment: async (newsDocumentId: string, commentDocumentId: string, content: string) => {
    try {
      const res = await fetch(`${baseUrl}/news-comments/${commentDocumentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            content,
          }
        }),
      });

      if (!res.ok) throw new Error('Failed to update comment');

      await get().fetchComments(newsDocumentId);
      return true;
    } catch (err) {
      console.error('Error updating comment:', err);
      return false;
    }
  },

  deleteComment: async (newsDocumentId: string, commentDocumentId: string) => {
    try {
      const res = await fetch(`${baseUrl}/news-comments/${commentDocumentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            deletedAt: new Date().toISOString(),
          }
        }),
      });

      if (!res.ok) throw new Error('Failed to delete comment');

      await get().fetchComments(newsDocumentId);
      return true;
    } catch (err) {
      console.error('Error deleting comment:', err);
      return false;
    }
  },

  toggleLike: async (newsDocumentId: string) => {
    try {
      const res = await fetch(`${baseUrl}/news-posts/${newsDocumentId}/toggle-like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      if (!res.ok) throw new Error('Failed to toggle like');

      const { data } = await res.json();

      set((state) => ({
        news: state.news.map(n =>
          n.documentId === newsDocumentId
            ? { ...n, likes: data.likeCount, liked_by: data.liked_by ?? (data.liked ? [...(n.liked_by || [])] : []) }
            : n
        ),
        groupNewsByGroupDocumentId: Object.fromEntries(
          Object.entries(state.groupNewsByGroupDocumentId).map(([groupDocumentId, posts]) => [
            groupDocumentId,
            posts.map((post) =>
              post.documentId === newsDocumentId
                ? { ...post, likes: data.likeCount, liked_by: data.liked_by ?? post.liked_by }
                : post
            ),
          ])
        ),
      }));
    } catch (err) {
      console.error('Error toggling like:', err);
    }
  },

  toggleCommentLike: async (newsDocumentId: string, commentDocumentId: string) => {
    try {
      const res = await fetch(`${baseUrl}/news-comments/${commentDocumentId}/toggle-like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      if (!res.ok) throw new Error('Failed to toggle comment like');

      const { data } = await res.json();

      set((state) => ({
        commentsByNews: {
          ...state.commentsByNews,
          [newsDocumentId]: (state.commentsByNews[newsDocumentId] || []).map(c =>
            c.documentId === commentDocumentId
              ? { ...c, likes: data.likeCount, liked_by: data.liked_by ?? (data.liked ? [...(c.liked_by || [])] : []) }
              : c
          ),
        },
      }));
    } catch (err) {
      console.error('Error toggling comment like:', err);
    }
  },

  hasUserLiked: (newsDocumentId: string, userId: number) => {
    const state = get();
    const likedInFeed = state.news.some((item) =>
      item.documentId === newsDocumentId && userHasLiked(item.liked_by, userId)
    );

    if (likedInFeed) {
      return true;
    }

    return Object.values(state.groupNewsByGroupDocumentId).some((posts) =>
      posts.some((post) => post.documentId === newsDocumentId && userHasLiked(post.liked_by, userId))
    );
  },

  hasUserLikedComment: (newsDocumentId: string, commentDocumentId: string, userId: number) => {
    const comments = get().commentsByNews[newsDocumentId] || [];
    const comment = comments.find(c => c.documentId === commentDocumentId);
    return userHasLiked(comment?.liked_by, userId);
  },

  setSelectedCategory: (_category: string | null) => {
    // Placeholder for future category filtering
  },

  getSortedNews: (isFollowedGroup: (id: string) => boolean) => {
    const news = get().news;
    const now = Date.now();
    const priorityWindowMs = 24 * 60 * 60 * 1000; // 24 hours

    // Priority bucket: followed group content created within the last 24 hours
    const priorityNews: News[] = [];
    const otherNews: News[] = [];

    news.forEach(item => {
      const followedGroup = item.group?.documentId ? isFollowedGroup(item.group.documentId) : false;
      const itemTime = new Date(item.createdAt).getTime();
      const ageMs = now - itemTime;
      const withinPriorityWindow = ageMs >= 0 && ageMs <= priorityWindowMs;

      if (followedGroup && withinPriorityWindow) {
        priorityNews.push(item);
      } else {
        otherNews.push(item);
      }
    });

    // Sort each group by date (newest first)
    const sortByDate = (a: News, b: News) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

    priorityNews.sort(sortByDate);
    otherNews.sort(sortByDate);

    // Followed content (24h window) first, then everything else
    return [...priorityNews, ...otherNews];
  },
}));

